import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { MaskedLines, Reveal, ParallaxImage } from "@/components/Motion";
import { imgSrc } from "@/lib/imgSrc";
import { currentSeason } from "@/lib/season";
import api from "@/lib/api";
import InstagramPopup from "@/components/InstagramPopup";
import { usePageMeta } from "@/lib/seo";
import { fmtHeight } from "@/lib/units";

const HERO_IMG = "/hero-duo.jpg";

function useCountUp(target, duration = 1400) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (target == null) return;
    let raf;
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min((t - start) / duration, 1);
      setValue(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return value;
}

function LiveStat({ value, label, testid, to }) {
  const n = useCountUp(value);
  const inner = (
    <>
      <span className="font-display font-light text-3xl sm:text-4xl tracking-tight" data-testid={testid}>{n}</span>
      <span className="overline-label !text-[10px]">{label}</span>
    </>
  );
  return to ? (
    <Link to={to} className="flex items-baseline gap-3 group">
      <span className="group-hover:text-accent transition-colors flex items-baseline gap-3">{inner}</span>
    </Link>
  ) : (
    <span className="flex items-baseline gap-3">{inner}</span>
  );
}

function LiveBoardStrip({ live }) {
  if (!live || live.enabled === false) return null;
  return (
    <div data-testid="live-board-strip" className="border-b border-white/10 px-6 sm:px-10 py-8 flex flex-wrap items-center gap-x-14 gap-y-5">
      <span className="flex items-center gap-3 overline-label !text-[10px] !text-white/85">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-60" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-accent" />
        </span>
        Live on the board
      </span>
      <LiveStat value={live.open_calls} label="open calls" testid="live-open-calls" to="/castings" />
      <LiveStat value={live.houses} label="houses casting" testid="live-houses" />
      <LiveStat value={live.applications_7d} label="applications this week" testid="live-applications" />
      <LiveStat value={live.faces} label="faces on the board" testid="live-faces" to="/models" />
    </div>
  );
}

function SuccessStrip({ bookings }) {
  if (!bookings || bookings.length === 0) return null;
  return (
    <section data-testid="success-strip" className="border-b border-white/10 px-6 sm:px-10 py-14">
      <p className="overline-label mb-8">Booked through Solis</p>
      <div className="flex flex-col gap-4">
        {bookings.map((b, i) => (
          <Reveal key={i} delay={i * 0.08}>
            <p className="font-display font-light text-xl sm:text-3xl tracking-tight" data-testid={`booking-${i}`}>
              {b.model} <span className="italic text-white/60">booked for</span> {b.casting}
              <span className="text-accent"> · {b.brand}</span>
            </p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  usePageMeta({
    title: "Solis Casting — Fashion Model Casting",
    description: "Solis Casting — the casting venue. Where houses, magazines and brands meet their next face. Reviewed casting calls, curated model books, open calls free to apply.",
    url: `${window.location.origin}/`,
  });
  const [models, setModels] = useState([]);
  const [castings, setCastings] = useState([]);
  const [live, setLive] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [heroUrl, setHeroUrl] = useState(HERO_IMG);

  useEffect(() => {
    api.get("/models").then((r) => setModels(r.data.slice(0, 4))).catch(() => {});
    api.get("/castings").then((r) => setCastings(r.data)).catch(() => {});
    api.get("/stats/live").then((r) => setLive(r.data)).catch(() => {});
    api.get("/stats/bookings").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/homepage/hero").then((r) => {
      const heroes = r.data?.heroes || {};
      const season = currentSeason();
      setHeroUrl(imgSrc(heroes[season] || heroes.default || HERO_IMG));
    }).catch(() => {});
  }, []);

  /* Split BEFORE slicing: platform calls always get their 12 slots and open calls
     their 4 — platform volume can never push open calls off the homepage */
  const platformCalls = castings.filter((c) => !c.external_url).slice(0, 12);
  const openCalls = castings.filter((c) => c.external_url).slice(0, 4);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="min-h-[92vh] grid grid-cols-1 lg:grid-cols-12 border-b border-white/10 overflow-hidden">
        <div className="lg:col-span-7 flex flex-col justify-center px-6 sm:px-10 py-24 lg:py-0">
          <p className="overline-label mb-10" data-testid="hero-overline">
            <span className="halloween-off christmas-off">Premium Casting Site — New York · Los Angeles · Paris</span>
            <span className="halloween-on halloween-flicker text-accent" data-testid="hero-overline-halloween">Haunted Season — New York · Los Angeles · Paris</span>
            <span className="christmas-on christmas-twinkle text-accent" data-testid="hero-overline-christmas">Festive Season — New York · Los Angeles · Paris</span>
          </p>
          <MaskedLines
            data-testid="hero-headline"
            lines={[
              <>WHERE THE WORLD</>,
              <>COMES TO FIND</>,
              <>ITS NEXT <span className="italic text-accent relative halloween-ember christmas-glow">FACE.
                <svg className="halloween-on halloween-drip" data-testid="halloween-drip" viewBox="0 0 60 22" aria-hidden="true">
                  <path fill="currentColor" d="M4 0 h2.6 v9 a1.3 1.3 0 0 1 -2.6 0 Z" />
                  <path fill="currentColor" d="M22 0 h3.2 v16 a1.6 1.6 0 0 1 -3.2 0 Z" />
                  <path fill="currentColor" d="M44 0 h2.6 v11 a1.3 1.3 0 0 1 -2.6 0 Z" />
                </svg>
                <svg className="christmas-on christmas-snowcap" data-testid="christmas-snowcap" viewBox="0 0 60 14" aria-hidden="true">
                  <path fill="currentColor" d="M0 6 Q4 0 9 4 Q13 -2 19 3 Q24 -1 30 3 Q36 -2 41 4 Q46 0 50 4 Q55 1 60 6 L60 8 Q55 5 50 8 Q45 4 40 8 Q35 4 30 8 Q24 4 19 8 Q13 4 9 8 Q4 5 0 8 Z" />
                </svg>
              </span></>,
            ]}
            className="font-display font-light text-5xl sm:text-7xl lg:text-8xl tracking-tighter leading-[0.95]"
          />
          <Reveal delay={0.8}>
            <p className="mt-10 max-w-md text-base text-muted-foreground leading-relaxed">
              Solis Casting connects vetted models with the houses, magazines and brands that define the
              season — from application to booking, in one venue.
            </p>
          </Reveal>
          <Reveal delay={1.0} className="mt-12 flex flex-wrap gap-4">
            <Link to="/auth?mode=register&role=model" data-testid="hero-apply-model-btn" className="btn-primary">
              Apply as a model <ArrowUpRight size={16} />
            </Link>
            <Link to="/auth?mode=register&role=employer" data-testid="hero-post-casting-btn" className="btn-outline">
              Post a casting <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
        <div className="lg:col-span-5 relative min-h-[60vh] lg:min-h-0">
          <ParallaxImage src={heroUrl} alt="Two models in black editorial looks" className="absolute inset-0" imgClassName="grayscale-[20%]" speed={12} />
          <div className="halloween-fog" data-testid="halloween-fog" aria-hidden="true" />
          <div className="christmas-snow" data-testid="christmas-snow" aria-hidden="true" />
          <div className="halloween-bats" data-testid="halloween-bats" aria-hidden="true">
            {[
              { top: "12%", delay: "0s", dur: "26s", size: 30 },
              { top: "30%", delay: "-9s", dur: "34s", size: 20 },
              { top: "6%", delay: "-18s", dur: "30s", size: 24 },
            ].map((b, i) => (
              <span key={i} className="bat-flight" style={{ top: b.top, animationDelay: b.delay, animationDuration: b.dur }}>
                <svg className="bat" width={b.size} height={b.size / 2} viewBox="0 0 32 16" fill="currentColor" aria-hidden="true">
                  <path d="M16 6 C13 2 8 1 4 3 C7 4 8 6 8 8 C5 8 2 9 0 12 C4 11 7 12 9 14 C10 12 13 11 16 11 C19 11 22 12 23 14 C25 12 28 11 32 12 C30 9 27 8 24 8 C24 6 25 4 28 3 C24 1 19 2 16 6 Z" />
                </svg>
              </span>
            ))}
          </div>
          <svg className="halloween-web" data-testid="halloween-web" viewBox="0 0 120 120" fill="none" stroke="currentColor" strokeWidth="1" aria-hidden="true">
            <path d="M120 0 L120 120" /><path d="M120 0 L74 111" /><path d="M120 0 L35 85" /><path d="M120 0 L9 46" /><path d="M120 0 L0 0" />
            <path d="M120 30 Q105 31 90 27" /><path d="M90 27 Q87 15 84 0" opacity="0.9" />
            <path d="M120 60 Q98 63 74 55" /><path d="M74 55 Q62 32 56 0" opacity="0.9" />
            <path d="M120 90 Q92 96 56 85" /><path d="M56 85 Q35 50 26 0" opacity="0.9" />
            <path d="M120 116 Q86 120 40 111" opacity="0.9" />
            <path d="M97 83 Q96 88 92 93" opacity="0.7" /><path d="M82 58 Q80 63 76 68" opacity="0.7" />
          </svg>
          <div className="absolute bottom-0 left-0 bg-background px-6 py-4 border-t border-r border-white/10">
            <p className="overline-label !text-[10px]">
              <span className="halloween-off christmas-off">SS27 — Open season</span>
              <span className="halloween-on text-accent" data-testid="hero-caption-halloween">SS27 — Haunted season</span>
              <span className="christmas-on text-accent" data-testid="hero-caption-christmas">SS27 — Festive season</span>
            </p>
          </div>
        </div>
      </section>

      {/* LIVE BOARD COUNTER */}
      <LiveBoardStrip live={live} />

      {/* INSTAGRAM ANNOUNCEMENT POPUP (once followed, never again) */}
      {live && live.popup !== false && <InstagramPopup />}

      {/* SUCCESS STORIES */}
      <SuccessStrip bookings={bookings} />

      {/* MARQUEE */}
      <div className="py-5 border-b border-white/10" data-testid="disciplines-marquee">
        <Marquee speed={28} gradient={false}>
          {["RUNWAY", "EDITORIAL", "CAMPAIGN", "COVER", "FITTING", "SHOWROOM", "FILM", "LOOKBOOK"].map((w) => (
            <span key={w} className="font-grotesk text-sm uppercase tracking-[0.4em] text-white/50 mx-10 flex items-center gap-10">
              {w} <span className="text-accent">·</span>
            </span>
          ))}
        </Marquee>
      </div>

      {/* CASTING CALLS — platform calls only */}
      <section className="border-t border-white/10 px-6 sm:px-10 py-32" data-testid="casting-calls-section">
        <Reveal className="flex items-end justify-between mb-16">
          <div>
            <p className="overline-label mb-4">Now Casting</p>
            <h2 className="font-display font-light text-4xl sm:text-6xl tracking-tight">Casting Calls</h2>
          </div>
          <Link to="/castings" data-testid="view-all-castings-link" className="hidden sm:inline-flex btn-outline !py-3">
            All castings <ArrowUpRight size={14} />
          </Link>
        </Reveal>
        <ThemeAwareCalls castings={platformCalls} gridTestId="casting-calls-grid" />
      </section>

      {/* OPEN CASTING & RUNWAY — external free-to-apply calls */}
      {openCalls.length > 0 && (
        <section className="border-t border-white/10 px-6 sm:px-10 py-32" data-testid="open-runway-section">
          <Reveal className="flex items-end justify-between mb-16">
            <div>
              <p className="overline-label mb-4">Free to apply — no membership needed</p>
              <h2 className="font-display font-light text-4xl sm:text-6xl tracking-tight">
                Open Casting <span className="italic text-accent">&amp;</span> Runway
              </h2>
            </div>
            <Link to="/castings" data-testid="view-open-calls-link" className="hidden sm:inline-flex btn-outline !py-3">
              All open calls <ArrowUpRight size={14} />
            </Link>
          </Reveal>
          <ThemeAwareCalls castings={openCalls} gridTestId="open-calls-grid" />
        </section>
      )}

      {/* THE BOARD */}
      <section className="border-t border-white/10 px-6 sm:px-10 py-32" data-testid="featured-models-section">
        <Reveal className="flex items-end justify-between mb-16">
          <div>
            <p className="overline-label mb-4">Selected Faces</p>
            <h2 className="font-display font-light text-4xl sm:text-6xl tracking-tight">Talent Board</h2>
          </div>
          <Link to="/models" data-testid="view-all-models-link" className="hidden sm:inline-flex btn-outline !py-3">
            View all <ArrowUpRight size={14} />
          </Link>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {models.map((m, i) => (
            <Reveal key={m.id} delay={i * 0.08}>
              <Link to={`/models/${m.slug || m.id}`} data-testid={`model-card-${m.id}`} className="group block bg-background">
                <div className="aspect-[3/4] overflow-hidden">
                  <img src={m.photo_url?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${m.photo_url}` : m.photo_url} alt={m.name} loading="lazy" decoding="async" className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" />
                </div>
                <div className="p-5 flex items-end justify-between">
                  <div>
                    <p className="font-display text-2xl">{m.name}</p>
                    <p className="overline-label !text-[11px] !font-semibold !text-white/80 mt-2">{m.city} · {fmtHeight(m.height_cm)}</p>
                  </div>
                  <ArrowUpRight size={18} className="text-white/30 group-hover:text-accent transition-colors" />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-white/10 px-6 sm:px-10 py-40 text-left" data-testid="cta-section">
        <Reveal>
          <p className="overline-label mb-8">Step into the room</p>
          <h2 className="font-display font-light text-4xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] max-w-4xl mb-14">
            The season is short. <span className="italic text-accent">The face is yours to find.</span>
          </h2>
          <div className="flex flex-wrap gap-4">
            <Link to="/auth?mode=register&role=model" data-testid="cta-model-btn" className="btn-primary">Join the board</Link>
            <Link to="/auth?mode=register&role=employer" data-testid="cta-employer-btn" className="btn-outline">Post a casting — 14-day free trial</Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}

function ThemeAwareCalls({ castings, gridTestId = "casting-calls-grid" }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border border-white/10" data-testid={gridTestId}>
      {castings.map((c, i) => (
        <Reveal key={c.id} delay={(i % 4) * 0.06}>
          <Link to={`/castings/${c.slug || c.id}`} data-testid={`casting-row-${c.id}`} className="group relative block aspect-[3/4] overflow-hidden border border-white/5">
            {c.image_url ? (
              <img src={imgSrc(c.image_url)} alt={c.title} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center p-6 border border-accent/40 m-px" data-testid={`casting-cover-${c.id}`}>
                <span className="inline-block w-2.5 h-2.5 bg-accent mb-4" />
                <p className="font-display font-light text-2xl text-center leading-tight group-hover:text-accent transition-colors duration-300">{c.title}</p>
                <p className="font-grotesk text-[10px] uppercase tracking-[0.15em] text-white/50 mt-4">{c.brand} · {c.compensation || "Rate TBD"}</p>
              </div>
            )}
            {c.featured && (
              <span className="absolute top-2 left-2 z-10 bg-accent text-accent-foreground font-grotesk text-[9px] uppercase tracking-[0.15em] px-2 py-1">Featured</span>
            )}
            {c.external_url && (
              <span className="absolute top-2 right-2 z-10 bg-black/70 backdrop-blur border border-accent text-accent font-grotesk text-[9px] uppercase tracking-[0.15em] px-2 py-1" data-testid={`free-apply-badge-${c.id}`}>
                Free to apply
              </span>
            )}
            {c.image_url && (
              <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black/70 to-transparent">
                <p className="font-display text-xl leading-tight">{c.title}</p>
                <p className="font-grotesk text-[10px] uppercase tracking-[0.15em] text-white/70 mt-1">{c.brand} · {c.compensation || "Rate TBD"}</p>
              </div>
            )}
          </Link>
        </Reveal>
      ))}
    </div>
  );
}

